import type { PassSummary } from '@sundkortid/shared';
import { PASS_LABELS_IS } from '@sundkortid/shared';

interface PassCardProps {
  pass: PassSummary;
  onClick?: () => void;
}

const statusLabels: Record<string, string> = {
  active: 'Virkt',
  expired: 'Útrunnið',
  fully_redeemed: 'Fullnýtt',
  revoked: 'Afturkallað',
};

const statusStyles: Record<string, string> = {
  active: 'bg-green-50 text-green-700',
  expired: 'bg-gray-100 text-gray-600',
  fully_redeemed: 'bg-amber-50 text-amber-700',
  revoked: 'bg-red-50 text-red-700',
};

const statusDots: Record<string, string> = {
  active: 'bg-green-500',
  expired: 'bg-gray-400',
  fully_redeemed: 'bg-amber-500',
  revoked: 'bg-red-500',
};

function PassTypeIcon({ type }: { type: string }) {
  if (type === 'single') {
    return (
      <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z" />
      </svg>
    );
  }
  if (type === 'ten_pack') {
    return (
      <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75 2.25 12l4.179 2.25m0-4.5 5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0 4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0-5.571 3-5.571-3" />
      </svg>
    );
  }
  if (type === 'monthly') {
    return (
      <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
      </svg>
    );
  }
  // annual
  return (
    <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
    </svg>
  );
}

export function PassCard({ pass, onClick }: PassCardProps) {
  const remaining = pass.totalEntries !== null ? pass.totalEntries - pass.usedEntries : null;
  const isClickable = onClick !== undefined && pass.status === 'active';
  const progressPercent = pass.totalEntries
    ? Math.round((pass.usedEntries / pass.totalEntries) * 100)
    : null;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border transition-all duration-200 ${
        pass.status === 'active'
          ? 'border-primary-200 bg-white shadow-md hover:shadow-lg'
          : 'border-gray-200 bg-gray-50/80'
      } ${isClickable ? 'cursor-pointer hover:scale-[1.01]' : ''}`}
      onClick={isClickable ? onClick : undefined}
    >
      {/* Top accent gradient bar */}
      <div
        className={`h-1.5 ${
          pass.status === 'active'
            ? 'bg-gradient-to-r from-primary via-secondary to-accent'
            : 'bg-gray-200'
        }`}
      />

      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-primary-400 mb-1.5">
              {PASS_LABELS_IS[pass.passType] ?? pass.passType}
            </p>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[pass.status] ?? ''}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${statusDots[pass.status] ?? ''}`} />
              {statusLabels[pass.status] ?? pass.status}
            </span>
          </div>
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
            <PassTypeIcon type={pass.passType} />
          </div>
        </div>

        {remaining !== null ? (
          <div className="mt-2">
            <div className="flex items-end gap-2 mb-3">
              <span className="text-4xl font-bold text-primary-900">{remaining}</span>
              <span className="text-sm text-gray-400 mb-1">af {pass.totalEntries} eftir</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-secondary transition-all duration-1000"
                style={{ width: `${100 - (progressPercent ?? 0)}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-lg font-semibold text-primary-900">Ótakmarkað</span>
            <span className="text-sm text-gray-400">aðgengi</span>
          </div>
        )}

        {pass.status === 'active' && onClick && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm font-medium text-primary-600">Opna QR kóða</span>
            <svg className="w-4 h-4 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}
